import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';

import MD5 from 'crypto-js/md5';
import { message } from 'antd';

import request from '@/request';

// 忘记密码表单负责短信验证和重置密码两步流程。
export default function ResetPwd({ onSuccess, onBack }) {
  const { user } = useSelector((state) => state.auth);
  const [step, setStep] = useState(1); // 1: 验证手机号, 2: 重置密码
  const [phone, setPhone] = useState(user?.phone || '');
  const [smsCode, setSmsCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [countDown, setCountDown] = useState(0);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const resetCode = useRef('');

  const isStep1Valid = phone && smsCode;
  const isStep2Valid =
    newPassword && confirmPassword && newPassword === confirmPassword;

  // 发送短信验证码，并在发送成功后开启倒计时。
  const handleSendCode = useCallback(async () => {
    if (!phone) {
      return message.open({
        type: 'warning',
        content: '请先输入手机号',
      });
    }
    if (countDown > 0 || isSendingCode) return;

    setIsSendingCode(true);

    try {
      await request.post('/auth/sms/code', {
        template: 'SMS_327844250',
        phone: String(phone).trim(),
      });
      message.open({
        type: 'success',
        content: '发送成功',
      });
      // 发送成功后开始倒计时
      setCountDown(60);
    } finally {
      setIsSendingCode(false);
    }
  }, [phone, countDown, isSendingCode]);

  // 维护验证码按钮倒计时。
  useEffect(() => {
    if (countDown <= 0) return;

    const t = setInterval(() => {
      setCountDown((s) => {
        if (s <= 1) {
          clearInterval(t);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [countDown]);

  // 校验短信验证码后进入新密码设置步骤。
  const handleStep1Submit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!isStep1Valid) return;

      setIsLoading(true);

      try {
        const result = await request.post('/auth/sms/code/token ', {
          template: 'SMS_327844250',
          phone: String(phone).trim(),
          code: smsCode,
        });
        if (result) resetCode.current = result;
        setStep(2);
      } finally {
        setIsLoading(false);
      }
    },
    [isStep1Valid, phone, smsCode]
  );

  // 提交新密码，保留原有加密与重置接口逻辑。
  const handleStep2Submit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!isStep2Valid) return;

      if (newPassword !== confirmPassword) {
        return message.open({
          type: 'warning',
          content: '两次输入的密码不一致',
        });
      }

      setIsLoading(true);

      try {
        await request.post('/auth/password/forget', {
          code: resetCode.current,
          newPassword: MD5(String(newPassword).trim()).toString(),
        });
        message.open({
          type: 'success',
          content: '重置成功',
        });
        onSuccess?.();
      } finally {
        setIsLoading(false);
      }
    },
    [isStep2Valid, newPassword, confirmPassword, onSuccess]
  );

  return (
    <form
      onSubmit={step === 1 ? handleStep1Submit : handleStep2Submit}
      className="w-full"
    >
      {step === 1 ? (
        <>
          <div className="w-full">
            <label
              htmlFor="phone"
              className={`flex h-[45px] items-start border-b text-left transition-colors ${
                focusedField === 'phone'
                  ? 'border-[#007eff]'
                  : 'border-[#ebebeb]'
              }`}
            >
              <span className="w-[86px] shrink-0 text-[14px] leading-[18px] text-[#333]">
                手机号
              </span>
              <input
                id="phone"
                type="tel"
                placeholder="输入手机号"
                maxLength={11}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onFocus={() => setFocusedField('phone')}
                onBlur={() => setFocusedField(null)}
                className="h-[18px] flex-1 bg-transparent text-[14px] leading-[18px] text-[#333] placeholder:text-[#999] focus:outline-none"
                required
              />
            </label>

            <div
              className={`mt-[31px] flex h-[45px] items-start border-b text-left transition-colors ${
                focusedField === 'sms'
                  ? 'border-[#007eff]'
                  : 'border-[#ebebeb]'
              }`}
            >
              <label
                htmlFor="sms"
                className="w-[86px] shrink-0 text-[14px] leading-[18px] text-[#333]"
              >
                验证码
              </label>
              <input
                id="sms"
                type="text"
                inputMode="numeric"
                placeholder="输入验证码"
                value={smsCode}
                onChange={(e) => setSmsCode(e.target.value)}
                onFocus={() => setFocusedField('sms')}
                onBlur={() => setFocusedField(null)}
                className="h-[18px] min-w-0 flex-1 bg-transparent text-[14px] leading-[18px] text-[#333] placeholder:text-[#999] focus:outline-none"
                required
              />

              <button
                type="button"
                onClick={handleSendCode}
                disabled={countDown > 0 || isSendingCode}
                className={`ml-3 shrink-0 cursor-pointer text-[14px] leading-[18px] transition-opacity ${
                  countDown > 0 || isSendingCode
                    ? 'text-[#999]'
                    : 'text-[#007eff] hover:opacity-80'
                }`}
              >
                {isSendingCode
                  ? '发送中'
                  : countDown > 0
                    ? `${countDown}s`
                    : '获取验证码'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || !isStep1Valid}
            className={`mt-[24px] flex h-[50px] w-full cursor-pointer items-center justify-center gap-2 rounded-[10px] text-[14px] leading-[18px] text-white transition-opacity ${
              isStep1Valid && !isLoading
                ? 'bg-[#0a0a0a] hover:opacity-90'
                : 'bg-[#0a0a0a] opacity-60'
            }`}
          >
            {isLoading && (
              <svg
                className="h-4 w-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            <span>{isLoading ? '验证中...' : '下一步'}</span>
          </button>
        </>
      ) : (
        <>
          <div className="w-full">
            <label
              htmlFor="newPassword"
              className={`flex h-[45px] items-start border-b text-left transition-colors ${
                focusedField === 'newPassword'
                  ? 'border-[#007eff]'
                  : 'border-[#ebebeb]'
              }`}
            >
              <span className="w-[86px] shrink-0 text-[14px] leading-[18px] text-[#333]">
                新密码
              </span>
              <input
                id="newPassword"
                type="password"
                placeholder="输入新密码"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onFocus={() => setFocusedField('newPassword')}
                onBlur={() => setFocusedField(null)}
                className="h-[18px] flex-1 bg-transparent text-[14px] leading-[18px] text-[#333] placeholder:text-[#999] focus:outline-none"
                required
              />
            </label>

            <label
              htmlFor="confirmPassword"
              className={`mt-[31px] flex h-[45px] items-start border-b text-left transition-colors ${
                focusedField === 'confirmPassword'
                  ? 'border-[#007eff]'
                  : 'border-[#ebebeb]'
              }`}
            >
              <span className="w-[86px] shrink-0 text-[14px] leading-[18px] text-[#333]">
                确认密码
              </span>
              <input
                id="confirmPassword"
                type="password"
                placeholder="再次输入新密码"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setFocusedField('confirmPassword')}
                onBlur={() => setFocusedField(null)}
                className="h-[18px] flex-1 bg-transparent text-[14px] leading-[18px] text-[#333] placeholder:text-[#999] focus:outline-none"
                required
              />
            </label>

            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <p className="mt-2 text-left text-[12px] leading-[18px] text-red-500">
                两次输入的密码不一致
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading || !isStep2Valid}
            className={`mt-[24px] flex h-[50px] w-full cursor-pointer items-center justify-center gap-2 rounded-[10px] text-[14px] leading-[18px] text-white transition-opacity ${
              isStep2Valid && !isLoading
                ? 'bg-[#0a0a0a] hover:opacity-90'
                : 'bg-[#0a0a0a] opacity-60'
            }`}
          >
            {isLoading && (
              <svg
                className="h-4 w-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            <span>{isLoading ? '重置中...' : '重置密码'}</span>
          </button>
        </>
      )}

      {onBack && (
        <div className="mt-[16px] flex justify-end">
          <button
            type="button"
            onClick={onBack}
            className="cursor-pointer text-[14px] leading-[18px] text-[#007eff] transition-opacity hover:opacity-80"
          >
            返回登录
          </button>
        </div>
      )}
    </form>
  );
}
